import { coerceIfInfinity } from "@citrine/utils/number";
import {
  SCIENTIFIC_HIGH,
  SCIENTIFIC_LOW,
  roundValue,
  trimExcessSignificance,
} from "@citrine/utils/rounding";
import { toRealString } from "@citrine/utils/text";

import type { ExplicitType } from "../../../../utils";
import { isOfType } from "../../../../utils";

// https://citrineinformatics.github.io/taurus-documentation/specification/value-types/#real-values
interface IRealValueBase {
  units?: string;
}

export interface INormalRealValue extends IRealValueBase {
  type: "normal_real";
  mean: number;
  std: number;
}

export interface IUniformRealValue extends IRealValueBase {
  type: "uniform_real";
  lower_bound: number;
  upper_bound: number;
}

export interface INominalRealValue extends IRealValueBase {
  type: "nominal_real";
  nominal: number | string;
}

export type IRealValue =
  | INormalRealValue
  | IUniformRealValue
  | INominalRealValue;

interface InfinityOptions {
  coerceInfinity?: boolean;
  infinityAsCharacter?: boolean;
}

/**
 * Turns a real value into a Nominal Real object. Coerces Number.MAX_VALUE to
 * Infinity by default.
 */
export const toNominalRealValue = (
  value: number,
  { coerceInfinity, infinityAsCharacter }: InfinityOptions = {}
): INominalRealValue => ({
  type: "nominal_real",
  nominal:
    (coerceInfinity ?? true)
      ? coerceIfInfinity(value, infinityAsCharacter)
      : value,
});

/**
 * Turns a real value into a Nominal Real object and returns a rounded string
 * representation. Coerces Number.MAX_VALUE to Infinity by default.
 */
export const toRoundedNominalRealValue = (
  value?: number,
  infinityOptions: InfinityOptions = {}
) =>
  value === undefined
    ? undefined
    : realValueRoundedToString(toNominalRealValue(value, infinityOptions));

// Declare string literals from DataModel
const DIMENSIONLESS = "dimensionless";

export const valueHasUnits = (value: IRealValue) => {
  if (value.type in ["normal_real", "uniform_real", "nominal_real"]) {
    return true;
  }
  return false;
};

// define typeguards
export const isNormalRealValue = isOfType<INormalRealValue>("normal_real");
export const isUniformRealValue = isOfType<IUniformRealValue>("uniform_real");
export const isNominalRealValue = isOfType<INominalRealValue>("nominal_real");

export const isRealValue = (
  value?: ExplicitType<string>
): value is IRealValue =>
  isNormalRealValue(value) ||
  isUniformRealValue(value) ||
  isNominalRealValue(value);
/** Format the 'value' part of a RealValue as a 'string' for rendering.
 */
export const realValueToString = (
  realValue: IRealValue | undefined
): string | undefined => {
  if (!realValue) {
    return "";
  }
  if (isNormalRealValue(realValue)) {
    return `${trimExcessSignificance(
      realValue.mean
    )} ± ${trimExcessSignificance(realValue.std)}`;
  }
  if (isUniformRealValue(realValue)) {
    return `[${trimExcessSignificance(
      realValue.lower_bound
    )}, ${trimExcessSignificance(realValue.upper_bound)}]`;
  }
  if (isNominalRealValue(realValue)) {
    return `${trimExcessSignificance(realValue.nominal)}`;
  }
};

/**
 * This function automatically rounds any value before converting it into a string
 * Used in places where you need a label or small version of the values
 *
 * Math for rounding provided by data team
 */
export const realValueRoundedToString = (
  realValue: IRealValue
): string | undefined => {
  if (
    (isNormalRealValue(realValue) && realValue.std === 0) ||
    (isUniformRealValue(realValue) &&
      realValue.lower_bound === realValue.upper_bound) ||
    isNominalRealValue(realValue)
  ) {
    // Nominal or zero-width distribution
    const nominal = isNormalRealValue(realValue)
      ? realValue.mean
      : isUniformRealValue(realValue)
        ? realValue.lower_bound
        : realValue.nominal;
    if (typeof nominal === "string") {
      return nominal;
    }

    const formatted = roundValue(nominal);
    return isNormalRealValue(realValue)
      ? toRealString(formatted, "0")
      : isUniformRealValue(realValue)
        ? realUniformStringsToString(formatted, formatted)
        : realNominalStringToString(formatted);
  }
  if (isNormalRealValue(realValue)) {
    // Leading digit of the number
    const meanLead = Math.floor(Math.log10(Math.abs(realValue.mean)));
    // Leading digit of the uncertainty
    const stdLead = Math.floor(Math.log10(realValue.std));
    // Last digit of interest
    const place = Math.floor(Math.log10(realValue.std) - 0.5);
    if (
      Math.max(meanLead, stdLead) < SCIENTIFIC_LOW ||
      Math.max(meanLead, stdLead) > SCIENTIFIC_HIGH
    ) {
      return toRealString(
        realValue.mean.toExponential(Math.max(0, meanLead - place)),
        realValue.std.toExponential(stdLead - place)
      );
    } else {
      return toRealString(
        realValue.mean.toFixed(Math.max(0, -place)),
        realValue.std.toFixed(Math.max(0, -place))
      );
    }
  }
  if (isUniformRealValue(realValue)) {
    // Leading digit of the uncertainty
    const lead = Math.floor(
      Math.log10(Math.max(-realValue.lower_bound, realValue.upper_bound))
    );

    // Last digit of interest
    const place = Math.floor(
      Math.log10(realValue.upper_bound - realValue.lower_bound) - 0.5
    );

    if (lead < SCIENTIFIC_LOW || lead > SCIENTIFIC_HIGH) {
      return realUniformStringsToString(
        realValue.lower_bound.toExponential(lead - place),
        realValue.upper_bound.toExponential(lead - place)
      );
    } else {
      return realUniformStringsToString(
        realValue.lower_bound.toFixed(Math.max(0, -place)),
        realValue.upper_bound.toFixed(Math.max(0, -place))
      );
    }
  }
};

// Given a string representation of .lowerBound and .upperBound, build the bigger string
export const realUniformStringsToString = (
  lowerBound: string,
  upperBound: string
): string => {
  return `[${lowerBound}, ${upperBound}]`;
};

// Given a string representation of .nominal, build the bigger string
export const realNominalStringToString = (nominal: string): string => {
  // This is a no-op, for consistency and future-proofing
  return nominal;
};
/** Extract string representation of "units" from a RealValue object
 *
 * Filter out "dimensionless" string for rendering
 */
export const realValueUnitToString = (
  realValue: IRealValue | undefined
): string => {
  if (!realValue) {
    return "";
  }

  if (realValue.units === DIMENSIONLESS) {
    return "";
  }
  return realValue.units || "";
};
